import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, TextField, FormControl,
  InputLabel, Select, MenuItem, IconButton, CircularProgress,
  makeStyles, Divider
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import { useSelector } from 'react-redux';
import { STATUS_API } from 'src/app/constant/config';

const useStyles = makeStyles(() => ({
  dialogTitle: {
    background: 'linear-gradient(135deg, #D9042F 0%, #a80020 100%)',
    color: '#fff',
    padding: '16px 24px'
  },
  titleText: { fontWeight: 700, fontSize: 17, color: '#fff' },
  closeBtn: { color: '#fff' },
  transferArrow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 0'
  },
  saveBtn: {
    background: 'linear-gradient(135deg, #D9042F 0%, #a80020 100%)',
    color: '#fff',
    fontWeight: 700,
    borderRadius: 8,
    textTransform: 'none',
    '&:hover': { background: 'linear-gradient(135deg, #b80025 0%, #8a001a 100%)' }
  }
}));

const StockTransferDialog = ({
  open,
  onClose,
  products = [],
  agencies = [],
  onTransfer
}) => {
  const classes = useStyles();
  const { statusTransfer } = useSelector(state => state.productSlice);

  const [form, setForm] = useState({
    product_id: '',
    from_agency_id: '',
    to_agency_id: '',
    quantity: '',
    note: ''
  });

  useEffect(() => {
    if (open) {
      setForm({ product_id: '', from_agency_id: '', to_agency_id: '', quantity: '', note: '' });
    }
  }, [open]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (!form.product_id || !form.from_agency_id || !form.to_agency_id || !form.quantity) return;
    if (form.from_agency_id === form.to_agency_id) {
      alert('Đại lý nguồn và đích không được trùng nhau!');
      return;
    }
    onTransfer({
      product_id: Number(form.product_id),
      from_agency_id: Number(form.from_agency_id),
      to_agency_id: Number(form.to_agency_id),
      quantity: Number(form.quantity),
      note: form.note || undefined
    });
  };

  const isLoading = statusTransfer === STATUS_API.PENDING;
  const fromAgencyName = agencies.find(a => String(a.id) === String(form.from_agency_id))?.name;
  const toAgencyName = agencies.find(a => String(a.id) === String(form.to_agency_id))?.name;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography className={classes.titleText}>🔄 Chuyển kho giữa đại lý</Typography>
          <IconButton size="small" className={classes.closeBtn} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" flexDirection="column" style={{ gap: 16, paddingTop: 8 }}>
          <FormControl variant="outlined" size="small" fullWidth required>
            <InputLabel>Sản phẩm *</InputLabel>
            <Select name="product_id" value={form.product_id} onChange={handleChange} label="Sản phẩm *">
              <MenuItem value="">-- Chọn sản phẩm --</MenuItem>
              {products.map(p => (
                <MenuItem key={p.id} value={p.id}>[{p.code}] {p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />

          {/* From/To agencies */}
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <FormControl variant="outlined" size="small" style={{ flex: 1 }} required>
              <InputLabel>Từ đại lý *</InputLabel>
              <Select name="from_agency_id" value={form.from_agency_id} onChange={handleChange} label="Từ đại lý *">
                <MenuItem value="">-- Nguồn --</MenuItem>
                {agencies
                  .filter(a => String(a.id) !== String(form.to_agency_id))
                  .map(a => (
                    <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                  ))
                }
              </Select>
            </FormControl>

            <Box className={classes.transferArrow}>
              <SwapHorizIcon style={{ color: '#D9042F', fontSize: 28 }} />
            </Box>

            <FormControl variant="outlined" size="small" style={{ flex: 1 }} required>
              <InputLabel>Đến đại lý *</InputLabel>
              <Select name="to_agency_id" value={form.to_agency_id} onChange={handleChange} label="Đến đại lý *">
                <MenuItem value="">-- Đích --</MenuItem>
                {agencies
                  .filter(a => String(a.id) !== String(form.from_agency_id))
                  .map(a => (
                    <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Box>

          {/* Summary */}
          {fromAgencyName && toAgencyName && (
            <Box
              style={{
                backgroundColor: '#fff0f3',
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 13,
                color: '#a80020'
              }}
            >
              <strong>{fromAgencyName}</strong> → <strong>{toAgencyName}</strong>
            </Box>
          )}

          <TextField
            label="Số lượng chuyển *"
            name="quantity"
            value={form.quantity}
            onChange={handleChange}
            variant="outlined"
            size="small"
            fullWidth
            type="number"
            inputProps={{ min: 1 }}
            required
          />

          <TextField
            label="Ghi chú"
            name="note"
            value={form.note}
            onChange={handleChange}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={2}
            placeholder="Lý do chuyển kho..."
          />
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: '12px 24px', gap: 8 }}>
        <Button onClick={onClose} variant="outlined" style={{ borderRadius: 8, textTransform: 'none' }}>
          Hủy
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          className={classes.saveBtn}
          disabled={
            isLoading ||
            !form.product_id ||
            !form.from_agency_id ||
            !form.to_agency_id ||
            !form.quantity ||
            form.from_agency_id === form.to_agency_id
          }
          startIcon={isLoading ? <CircularProgress size={16} style={{ color: '#fff' }} /> : null}
        >
          {isLoading ? 'Đang chuyển...' : 'Xác nhận chuyển kho'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockTransferDialog;
